
import LocationHeader from '@root/modules/weather/components/location/LocationHeader.vue';
import TableSwitch from '@root/modules/weather/components/table/TableSwitch.vue';
import TableHours from '@root/modules/weather/components/table/TableHours.vue';
import AdsLayout from '@root/common/components/layout/AdsLayout.vue';

import Page from '@root/common/base/Page';
import WeatherService from '@root/modules/weather/services/Weather.service';
import LocationsService from '@root/modules/weather/services/Locations.service';
import { Weather } from '@root/modules/weather/typings/weather';
import { toDailyForecast, toHourlyForecast } from '@root/modules/weather/utils/weatherParser';
import getFrontPageAdsSettings from '@root/modules/frontpage/utils/getFrontPageAdsSettings';
import isMobileView from '@root/common/utils/isMobileView';
import ListSkeletonLoader from '@root/common/components/base/loader/ListSkeletonLoader.vue';

interface Variables {
  placeId?: string;
  lon?: string;
  lat?: string;
}

interface Data {
  data: Weather.Data;
  dataType: string;
  variables: Variables;
  weather: Record<string, unknown> | null;
  meta: Record<string, any>;
  isMobileView: boolean;
}

interface Computed {
  variablesLocal: Variables;
}

interface Methods {
  getDataType: (type: string) => void;
  getGeoLocation: (location: Variables) => void;
  getWeather: () => void;
  toDailyForecast: typeof toDailyForecast;
  toHourlyForecast: typeof toHourlyForecast;
}

export default Page.extend<Data, Methods, Computed>({
  beforeRouteLeave(to, from, next) {
    this.resetPageAdsSettings();

    next();
  },
  components: {
    LocationHeader,
    TableSwitch,
    TableHours,
    AdsLayout,
    ListSkeletonLoader,
  },
  data() {
    return {
      data: null,
      dataType: 'hours',
      variables: {
        placeId: this.$route.params.id || 'ChIJ9QhkXPuT3UYRQNzpcIzRAAQ',
      },
      weather: null,
      meta: {
        title: 'Delfi orai',
      },
      isMobileView: false,
    };
  },
  computed: {
    variablesLocal() {
      return this.variables;
    },
  },
  mounted() {
    this.isMobileView = isMobileView();
  },
  methods: {
    getDataType(type: string) {
      this.dataType = type;
    },
    getGeoLocation(location) {
      this.variables = location;
      this.getWeather();
    },
    async getWeather() {
      const weatherService = new WeatherService();
      const data = await weatherService.fetch({ hourlyTime: 100, dailyTime: 100, ...this.variablesLocal });
      if (data) {
        this.weather = data;
      }
    },
    toDailyForecast,
    toHourlyForecast,
  },
  async fetch() {
    const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
    const { params } = this.$route;
    const placeId = params.id.replace(/\//gi, '-');
    const variables = {
      hourlyTime: 100,
      dailyTime: 100,
      placeId,
    };

    try {
      if (variables.placeId && !/,/.test(variables.placeId)) {
        const locationsService = new LocationsService();
        const locations = await locationsService.fetch({ locationName: variables.placeId });
        if (locations && locations.length) {
          variables.placeId = locations[0].placeId;
        } else {
          const { baseUrl } = this.$channelConfig('redirects');
          const pageUrl = !fakeDomainEnabled ? `${baseUrl}/orai` : `/orai`;
          process.server ? this.$nuxt.context.redirect(302, pageUrl) : window.location.replace(pageUrl);
        }
      }

      const tempData = this.$store.getters['weather/getTempData'];
      const weatherService = new WeatherService();
      const data = tempData || (await weatherService.fetch(variables));

      if (tempData) {
        this.$store.commit('weather/resetTempData');
      }

      this.weather = Object.freeze(data) as Weather.Data;

      const location = this.weather!.location as Record<string, unknown>;
      this.meta.title = `${location!.name as string} ${this.$t('weather.titleWeatherForecast')} - ${this.meta.title}`;

      this.setPageAdsSettings(getFrontPageAdsSettings({ $channelConfig: this.$channelConfig }));
    } catch (e) {
      this.handlePageError(e);
    }
  },
  head() {
    const meta = this.getPageMeta();

    return {
      title: this.meta.title,
      meta: meta.data,
      link: meta.link,
    };
  },
});
